import { ReservedFields } from "@/onboarding/features/product/models/Product"

// Sections whose fields carry port-scoped components: candidates for an
// apply of these fields are restricted to the source tour's port (a venue in
// one port cannot attach to a tour in another). Non-component sections may
// target any port.
export const COMPONENT_FIELD_NAMES: Set<string> = new Set([
  ReservedFields.LOGISTICAL_COMPONENTS,
  ReservedFields.VENUE_COMPONENTS,
  ReservedFields.REFRESHMENT_COMPONENTS,
  ReservedFields.RESTAURANT_COMPONENTS,
  ReservedFields.ENTERTAINMENT_COMPONENTS,
  ReservedFields.GUIDE_COMPONENTS,
  ReservedFields.HEADSET_COMPONENTS,
])

export const isComponentFieldSet = (fieldNames: string[]): boolean =>
  fieldNames.some((name) => COMPONENT_FIELD_NAMES.has(name))

export interface CandidateOption {
  label: string
  value: string
  group: string
  componentEditingLocked: boolean
}

// Fixed section order for the picker, mirroring the backend's
// importable_product_group vocabulary; unknown groups sink to the end.
export const GROUP_ORDER = [
  "Same tour code (other brands)",
  "This port",
  "Nearby ports (same country)",
  "Other ports",
]

const groupRank = (group: string): number => {
  const rank = GROUP_ORDER.indexOf(group)
  return rank === -1 ? GROUP_ORDER.length : rank
}

export interface TargetDiff {
  productId: string
  outcome: "clean" | "overwrite" | "no_editable_bid"
  changedFields: { name: string; overwrites: boolean }[]
}

export type RowState = "clean" | "overwrite" | "locked" | "unavailable"

export interface TargetRow {
  productId: string
  label: string
  group: string
  state: RowState
  selectable: boolean
  changedFieldCount: number
  componentEditingLocked: boolean
}

// Deliberate row ordering: the select-all set first, then overwrites (each
// needs its own tick), then rows that cannot be selected at all. Candidates
// arrive alphabetical from the backend, keeping each group alphabetical too.
const STATE_ORDER: Record<RowState, number> = {
  clean: 0,
  overwrite: 1,
  locked: 2,
  unavailable: 3,
}

// Locked wins over the diff outcome for component field sets: the write path
// would refuse anyway, so the row is disabled with the reason rather than
// inviting a doomed selection.
const deriveState = (
  componentEditingLocked: boolean,
  diff: TargetDiff | undefined,
  componentFieldSet: boolean
): RowState => {
  if (componentFieldSet && componentEditingLocked) {
    return "locked"
  }
  if (!diff || diff.outcome === "no_editable_bid") {
    return "unavailable"
  }
  return diff.outcome
}

// Merges the candidate list with the per-target diffs into picker rows.
export const buildRows = (
  options: CandidateOption[],
  diffsById: Record<string, TargetDiff>,
  componentFieldSet: boolean
): TargetRow[] =>
  options
    .map((option) => {
      const diff = diffsById[option.value]
      const state = deriveState(option.componentEditingLocked, diff, componentFieldSet)

      return {
        productId: option.value,
        label: option.label,
        group: option.group,
        state,
        selectable: state === "clean" || state === "overwrite",
        changedFieldCount: diff ? diff.changedFields.length : 0,
        componentEditingLocked: option.componentEditingLocked,
      }
    })
    .sort(
      (a, b) =>
        groupRank(a.group) - groupRank(b.group) || STATE_ORDER[a.state] - STATE_ORDER[b.state]
    )

// Re-derives a previously built row against a fresh diff (the field set may
// have changed since the row was built).
export const refreshRow = (
  row: TargetRow,
  diff: TargetDiff | undefined,
  componentFieldSet: boolean
): TargetRow => {
  const state = deriveState(row.componentEditingLocked, diff, componentFieldSet)

  return {
    ...row,
    state,
    selectable: state === "clean" || state === "overwrite",
    changedFieldCount: diff ? diff.changedFields.length : 0,
  }
}

// Auto-deselect on a section-set change: a selected tour is kept only while
// applying stays non-surprising — still clean, or an overwrite the operator
// already ticked as an overwrite. Anything that degrades (clean → overwrite,
// selectable → locked/unavailable) is dropped and counted for the notice.
// When the field set turns component-scoped (scopeTightened), rows outside
// the fresh port-scoped results can no longer be verified against the source
// port, so hidden selections are dropped too.
export const reconcileSelection = (
  selected: Map<string, TargetRow>,
  rows: TargetRow[],
  diffsById: Record<string, TargetDiff>,
  componentFieldSet: boolean,
  scopeTightened: boolean
): { next: Map<string, TargetRow>; droppedCount: number } => {
  const visibleById = new Map(rows.map((row) => [row.productId, row]))
  const next = new Map<string, TargetRow>()
  let droppedCount = 0

  selected.forEach((oldRow, id) => {
    const visible = visibleById.get(id)
    const fresh = visible ?? refreshRow(oldRow, diffsById[id], componentFieldSet)
    const keep =
      !(scopeTightened && !visible) &&
      (fresh.state === "clean" || (fresh.state === "overwrite" && oldRow.state === "overwrite"))

    if (keep) {
      next.set(id, fresh)
    } else {
      droppedCount += 1
    }
  })

  return { next, droppedCount }
}

// Group select deliberately picks only clean rows: an overwrite must be
// ticked row by row (the "dangerous oversmear" guard). Selection is scoped to
// one group — "clean" only means no conflict, not that the tour should get
// these values, and the groups are the real intent cohorts.
export const groupCleanRows = (rows: TargetRow[], group: string): TargetRow[] =>
  rows.filter((row) => row.group === group && row.state === "clean")

export const summarizeReport = (report: { outcome: string }[]): Record<string, number> =>
  report.reduce(
    (counts, entry) => ({ ...counts, [entry.outcome]: (counts[entry.outcome] ?? 0) + 1 }),
    {} as Record<string, number>
  )
